import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import {
  shouldPrimeForPush,
  requestPushPermission,
  markAskedForPush,
} from '../../utils/pushClient';

// Contextual pre-permission sheet (Apple HIG: prime BEFORE the system prompt,
// with one primary action, at a moment the purpose is obvious). Mounted once at
// app root; it listens for the 'petzy:push-prompt' window event (fired after a
// successful booking) and shows itself only when we've never asked and iOS can
// still be prompted. "Activează" opens the real system prompt; "Mai târziu"
// dismisses. Shown at most once per install.
export default function PushPrimingSheet() {
  const [open, setOpen] = useState(false);
  const [shown, setShown] = useState(false); // drives the slide/fade
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const onTrigger = async () => {
      if (open) return;
      if (await shouldPrimeForPush()) {
        markAskedForPush(); // show once ever, whatever the outcome
        setOpen(true);
        requestAnimationFrame(() => setShown(true));
      }
    };
    window.addEventListener('petzy:push-prompt', onTrigger);
    return () => window.removeEventListener('petzy:push-prompt', onTrigger);
  }, [open]);

  const close = useCallback(() => {
    setShown(false);
    setTimeout(() => setOpen(false), 260);
  }, []);

  // Escape dismisses the sheet — expected behavior for an aria-modal dialog on
  // the surfaces this reaches that have a hardware keyboard (Mac Catalyst, iPad
  // + keyboard); a no-op on touch-only devices. Consistent with the backdrop /
  // "Mai târziu" paths — "asked" was already marked when the sheet opened.
  useEffect(() => {
    if (!open) return undefined;
    const onKeyDown = (e) => { if (e.key === 'Escape') close(); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [open, close]);

  const enable = useCallback(async () => {
    setBusy(true);
    const result = await requestPushPermission();
    setBusy(false);
    close();
    if (result === 'denied') {
      toast('Poți activa notificările oricând din Setări › Petzy.', { icon: '🔔' });
    }
  }, [close]);

  if (!open) return null;

  return (
    <div
      className="fixed inset-0 z-[120] flex items-end justify-center"
      role="dialog"
      aria-modal="true"
      aria-label="Activează notificările"
    >
      <div
        onClick={close}
        className={`absolute inset-0 bg-black/40 transition-opacity duration-200 motion-reduce:transition-none ${shown ? 'opacity-100' : 'opacity-0'}`}
      />
      <div
        className={`relative w-full max-w-md rounded-t-3xl bg-[#FAF5EF] px-6 pt-7 shadow-2xl transition-transform duration-300 ease-out motion-reduce:transition-none ${shown ? 'translate-y-0' : 'translate-y-full'}`}
        style={{ paddingBottom: 'calc(1.5rem + env(safe-area-inset-bottom))' }}
      >
        <div className="mx-auto mb-5 flex h-14 w-14 items-center justify-center rounded-2xl bg-[#E85D2A]/10 text-[#E85D2A]">
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
            <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
          </svg>
        </div>
        <h2 className="text-center text-xl font-semibold text-[#221D18]" style={{ textWrap: 'balance' }}>
          Rămâi la curent cu programările
        </h2>
        <p className="mx-auto mt-2 max-w-xs text-center text-[15px] leading-relaxed text-[#6B6157]">
          Primești o notificare când salonul îți confirmă, mută sau anulează programarea — plus un reminder înainte de vizită.
        </p>
        <button
          type="button"
          onClick={enable}
          disabled={busy}
          className="mt-6 w-full rounded-full bg-[#221D18] py-3.5 text-[15px] font-semibold text-[#FAF5EF] transition-opacity active:opacity-90 disabled:opacity-60"
        >
          {busy ? 'Se activează…' : 'Activează notificările'}
        </button>
        <button
          type="button"
          onClick={close}
          disabled={busy}
          className="mt-1.5 w-full py-3 text-[14px] font-medium text-[#9A8F82] active:opacity-70"
        >
          Mai târziu
        </button>
      </div>
    </div>
  );
}
